import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, MessageSquareWarning, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { useT, Bi } from "@/lib/i18n";
import { dateLabel } from "@/lib/format";
import { Card, EmptyState, ErrorState, ListSkeleton, PageTitle } from "@/components/common";
import { Field, BigInput, Chips } from "@/components/form";
import { BottomSheet } from "@/components/BottomSheet";
import { BackLink } from "@/pages/More";

const CATS = ["electrical", "plumbing", "food", "cleaning", "wifi", "other"];
const STATUS_CLS: Record<string, string> = {
  open: "bg-rose-50 text-rose-700 border-rose-200", in_progress: "bg-amber-50 text-amber-800 border-amber-200", resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function NewComplaint({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const { property } = useAuth();
  const tenants = useApi<any[]>(open && property ? `/tenants?property_id=${property.id}` : null);
  const [f, setF] = useState<any>({ title: "", category: "other", description: "", tenant_id: "none" });
  const save = async () => {
    try {
      await api.post(`/properties/${property.id}/complaints`, { ...f, tenant_id: f.tenant_id === "none" ? null : f.tenant_id });
      setF({ title: "", category: "other", description: "", tenant_id: "none" });
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && onClose()} title={<Bi k="new_complaint" />} testId="complaint-sheet">
      <div className="space-y-4">
        <Field label={t("title")}><BigInput data-testid="complaint-title-input" maxLength={100} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label={t("category")}>
          <Chips testId="complaint-category" value={f.category} onChange={(v) => setF({ ...f, category: v })} options={CATS.map((c) => ({ value: c, label: t(`cat_${c}` as any) }))} />
        </Field>
        <Field label={t("tenant_optional")}>
          <Select value={f.tenant_id} onValueChange={(v) => setF({ ...f, tenant_id: v })}>
            <SelectTrigger data-testid="complaint-tenant-select" className="h-12 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-</SelectItem>
              {(tenants.data || []).map((x) => <SelectItem key={x.id} value={x.id}>{x.name} · {x.room_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={`${t("description")} (${t("optional")})`}><Textarea data-testid="complaint-desc-input" rows={3} maxLength={1000} className="rounded-xl text-base bg-white" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Button data-testid="complaint-save-btn" disabled={!f.title.trim()} onClick={save} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">{t("save")}</Button>
      </div>
    </BottomSheet>
  );
}

function ComplaintCard({ c, onStatus }: { c: any; onStatus: (s: string) => void }) {
  const { t, lang } = useT();
  const next = c.status === "open" ? ["in_progress", "start_work"] : c.status === "in_progress" ? ["resolved", "resolve"] : ["open", "reopen"];
  return (
    <Card className="p-4" data-testid={`complaint-${c.id}`}>
      <div className="flex justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold">{c.title}</p>
          <p className="text-xs text-slate-500">{t(`cat_${c.category}` as any)}{c.tenant_name ? ` · ${c.tenant_name}` : ""} · {dateLabel(c.created_at, lang)}</p>
        </div>
        <span data-testid={`complaint-status-${c.id}`} className={`h-fit text-xs font-semibold rounded-full border px-2.5 py-1 whitespace-nowrap ${STATUS_CLS[c.status]}`}>{t(`c_${c.status}` as any)}</span>
      </div>
      {c.description && <p className="text-sm text-slate-600 mt-2">{c.description}</p>}
      <Button data-testid={`complaint-action-${c.id}`} variant="outline" onClick={() => onStatus(next[0])} className="mt-3 h-11 rounded-xl w-full">
        {next[0] === "resolved" && <CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" />}{t(next[1] as any)}
      </Button>
    </Card>
  );
}

export default function Complaints() {
  const { t } = useT();
  const { property } = useAuth();
  const { data, loading, error, reload } = useApi<any[]>(property ? `/properties/${property.id}/complaints` : null);
  const [filter, setFilter] = useState("active");
  const [open, setOpen] = useState(false);
  const setStatus = async (id: string, status: string) => {
    try { await api.patch(`/complaints/${id}`, { status }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const list = (data || []).filter((c) => filter === "all" || (filter === "active" ? c.status !== "resolved" : c.status === "resolved"));
  return (
    <div>
      <BackLink />
      <PageTitle title={t("complaints")} sub={property?.name} right={<Button data-testid="complaint-new-btn" onClick={() => setOpen(true)} className="h-11 rounded-full bg-emerald-700 hover:bg-emerald-800"><Plus className="h-4 w-4 mr-1" />{t("add")}</Button>} />
      <div className="mb-4"><Chips testId="complaints-filter" value={filter} onChange={setFilter} options={[{ value: "active", label: t("c_open") }, { value: "resolved", label: t("c_resolved") }, { value: "all", label: t("all") }]} /></div>
      {loading && !data ? <ListSkeleton rows={3} /> : error ? <ErrorState message={error} onRetry={reload} /> : !list.length ? (
        <EmptyState testId="complaints-empty" icon={MessageSquareWarning} title={t("no_complaints")} />
      ) : <div className="space-y-2.5 pg-stagger">{list.map((c) => <ComplaintCard key={c.id} c={c} onStatus={(s) => setStatus(c.id, s)} />)}</div>}
      <NewComplaint open={open} onClose={() => setOpen(false)} onSaved={reload} />
    </div>
  );
}
